// ========================
// Executive Overview - CM Performance API Types
// ========================

// Action type constants
export const ExecutiveOverviewTypes = {
  // CM Performance
  GET_CM_PERFORMANCE_REQUEST: "GET_CM_PERFORMANCE_REQUEST",
  GET_CM_PERFORMANCE_SUCCESS: "GET_CM_PERFORMANCE_SUCCESS",
  GET_CM_PERFORMANCE_FAILURE: "GET_CM_PERFORMANCE_FAILURE",

  // PHIC Trend Filters
  GET_PHIC_TREND_FILTERS_REQUEST: "GET_PHIC_TREND_FILTERS_REQUEST",
  GET_PHIC_TREND_FILTERS_SUCCESS: "GET_PHIC_TREND_FILTERS_SUCCESS",
  GET_PHIC_TREND_FILTERS_FAILURE: "GET_PHIC_TREND_FILTERS_FAILURE",

  // PHIC Trend Chart Data
  GET_PHIC_TREND_DATA_REQUEST: "GET_PHIC_TREND_DATA_REQUEST",
  GET_PHIC_TREND_DATA_SUCCESS: "GET_PHIC_TREND_DATA_SUCCESS",
  GET_PHIC_TREND_DATA_FAILURE: "GET_PHIC_TREND_DATA_FAILURE",
};

// ========================
// API Request Parameters
// ========================
export type CMPerformancePeriod = "current_week" | "last_week" | "current_month" | "last_month";

export interface CMPerformanceRequestParams {
  period: CMPerformancePeriod;
  date: string; // ISO date string, e.g. "2026-05-18"
}

// ========================
// API Response Types
// ========================
export type RiskStatus = "green" | "yellow" | "red";

export interface CMPerformanceRiskTableItem {
  operation: string;
  cm: string;
  cm_location: string;
  indicators: {
    output_hit_rate: RiskStatus;
    machine_downtime: RiskStatus;
    test_fpy: RiskStatus;
    assembly_fpy: RiskStatus;
  };
}

export interface CMPerformancePHICSummary {
  new: number;
  open: number;
  acknowledged: number;
  closed: number;
}

export interface CMPerformancePHICRecord {
  date: string;
  cm: string;
  cm_location: string;
  program: string;
  stage: string;
  alert_type: string;
  alert_reason: string;
  status: "new" | "open" | "acknowledged" | "closed";
  cause_type: string;
  dri: string;
  station: string;
}

export interface CMPerformanceData {
  risk_table: CMPerformanceRiskTableItem[];
  phic_summary: CMPerformancePHICSummary;
  phic_records: CMPerformancePHICRecord[];
}

export interface CMPerformanceResponse {
  status: string;
  data: CMPerformanceData;
}

// ========================
// PHIC Trend Filters API Types
// ========================
export interface PHICTrendFilterCombination {
  operation: string;
  cm: string;
  location: string;
}

export interface PHICTrendFiltersData {
  combinations: PHICTrendFilterCombination[];
}

export interface PHICTrendFiltersResponse {
  data: PHICTrendFiltersData;
}

// ========================
// PHIC Trend Chart Data API Types
// ========================
export type PHICTrendGroupBy = "program" | "cause_type" | "alert_type";

export interface PHICTrendDataRequestParams {
  start_time: string; // YYYY-MM format
  end_time: string;   // YYYY-MM format
  group_by?: PHICTrendGroupBy; // optional, default: program
  operation?: string; // optional filter
  cm?: string;        // optional filter
  location?: string;  // optional filter
}

export interface PHICTrendSeriesItem {
  name?: string;        // present when group_by=program (e.g., "D63-Foxconn-Kunshan")
  program: string;
  cm: string;
  location: string;
  operation?: string;
  alert_type?: string;  // present when group_by=alert_type
  cause_type?: string;  // present when group_by=cause_type
  data: number[];       // array of counts corresponding to months[]
}

export interface PHICTrendChartData {
  months: string[];              // e.g., ["2025-06", "2025-07", ...]
  series: PHICTrendSeriesItem[];
  totals: number[];              // total per month
}

export interface PHICTrendDataResponse {
  data: PHICTrendChartData;
}

// ========================
// Redux State
// ========================
export interface ExecutiveOverviewState {
  cmPerformance: {
    data: CMPerformanceData | null;
    loading: boolean;
    error: string | null;
  };
  phicTrendFilters: {
    data: PHICTrendFiltersData | null;
    loading: boolean;
    error: string | null;
  };
  phicTrendData: {
    data: PHICTrendChartData | null;
    loading: boolean;
    error: string | null;
  };
}
